import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Question {
  id: string;
  question_text: string;
  help_text: string | null;
  display_order: number;
}

interface Answer {
  id: string;
  question_id: string;
  answer_text: string;
  tag_weights: Record<string, number>;
  display_order: number;
}

interface QuizResult {
  id: string;
  result_persona_name: string;
  result_title: string;
  result_text: string;
  min_tag_thresholds: Record<string, number>;
  recommended_modules: Array<{ id: string; priority: number; reason?: string }>;
}

interface BedarfsAnalysatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BedarfsAnalysatorDialog = ({ open, onOpenChange }: BedarfsAnalysatorDialogProps) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer[]>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [calculatedTags, setCalculatedTags] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchQuizData();
    }
  }, [open]);

  const fetchQuizData = async () => {
    try {
      const { data: questionsData, error: questionsError } = await supabase
        .from("placement_quiz_questions")
        .select("*")
        .order("display_order");

      if (questionsError) throw questionsError;

      const { data: answersData, error: answersError } = await supabase
        .from("placement_quiz_answers")
        .select("*")
        .order("display_order");

      if (answersError) throw answersError;

      const groupedAnswers: Record<string, Answer[]> = {};
      answersData?.forEach((answer) => {
        if (!groupedAnswers[answer.question_id]) {
          groupedAnswers[answer.question_id] = [];
        }
        groupedAnswers[answer.question_id].push({
          ...answer,
          tag_weights: answer.tag_weights as Record<string, number>,
        });
      });

      setQuestions(questionsData || []);
      setAnswers(groupedAnswers);
    } catch (error) {
      console.error("Error fetching quiz data:", error);
      toast.error("Fehler beim Laden des Quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: string, answerId: string) => {
    setSelectedAnswers({ ...selectedAnswers, [questionId]: answerId });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      calculateResult();
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const calculateResult = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Calculate tag weights
      const tags: Record<string, number> = {};
      Object.entries(selectedAnswers).forEach(([questionId, answerId]) => {
        const answer = answers[questionId]?.find((a) => a.id === answerId);
        if (answer) {
          Object.entries(answer.tag_weights).forEach(([tag, weight]) => {
            tags[tag] = (tags[tag] || 0) + weight;
          });
        }
      });

      setCalculatedTags(tags);

      // Fetch all results and find the best match
      const { data: resultsData, error: resultsError } = await supabase
        .from("placement_quiz_results")
        .select("*");

      if (resultsError) throw resultsError;

      // Find result that matches thresholds (Score-based matching)
      // Sort results by threshold count (more specific personas first)
      const sortedResults = resultsData?.sort((a, b) => {
        const thresholdsA = a.min_tag_thresholds as Record<string, number>;
        const thresholdsB = b.min_tag_thresholds as Record<string, number>;
        return Object.keys(thresholdsB).length - Object.keys(thresholdsA).length;
      });

      const matchingResult = sortedResults?.find((result) => {
        const thresholds = result.min_tag_thresholds as Record<string, number>;
        
        // Empty thresholds = Fallback persona (matches everything)
        if (Object.keys(thresholds).length === 0) return true;
        
        // Check if user's score meets ALL threshold requirements
        return Object.entries(thresholds).every(([tag, minValue]) => {
          return (tags[tag] || 0) >= minValue;
        });
      });

      if (matchingResult) {
        setResult({
          ...matchingResult,
          min_tag_thresholds: matchingResult.min_tag_thresholds as Record<string, number>,
          recommended_modules: matchingResult.recommended_modules as Array<{ 
            id: string; 
            priority: number; 
            reason?: string;
          }>,
        });

        // Save attempt to database
        const { data: { user } } = await supabase.auth.getUser();
        const { data: attemptData } = await supabase.from("placement_quiz_attempts").insert({
          user_id: user?.id || null,
          answers: Object.values(selectedAnswers),
          calculated_tags: tags,
          result_id: matchingResult.id,
        }).select().single();

        // V2.1: Save result to user profile if logged in
        if (user?.id) {
          const { error: userResultError } = await supabase
            .from("placement_quiz_user_results")
            .upsert({
              user_id: user.id,
              attempt_id: attemptData?.id,
              result_persona_id: matchingResult.id,
              persona_name: matchingResult.result_persona_name,
              persona_title: matchingResult.result_title,
              recommended_modules: matchingResult.recommended_modules,
              user_tags: tags,
              completed_at: new Date().toISOString(),
            }, {
              onConflict: 'user_id'
            });

          if (userResultError) {
            console.error("Error saving user result:", userResultError);
          }
        }
      } else {
        toast.error("Keine passende Empfehlung gefunden");
      }
    } catch (error) {
      console.error("Error calculating result:", error);
      toast.error("Fehler bei der Auswertung");
    }
  };

  const handleReset = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setCalculatedTags({});
    setResult(null);
  };

  const handleModuleClick = (moduleId: string) => {
    onOpenChange(false);
    
    // Map module IDs to routes
    const moduleRoutes: Record<string, string> = {
      "main-program": "/programm",
      "datenschutz-kompass": "/datenschutz-kompass-info",
      "mitbestimmung": "/mitbestimmung-info",
      "ki-grundlagen": "/modul0",
    };

    const route = moduleRoutes[moduleId] || "/programm";
    navigate(route);
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <p>Lädt...</p>
        </DialogContent>
      </Dialog>
    );
  }

  if (questions.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bedarfs-Analysator</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Der Bedarfs-Analysator ist derzeit nicht verfügbar. Bitte kontaktieren Sie den Administrator.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswers = answers[currentQuestion.id] || [];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const isAnswerSelected = selectedAnswers[currentQuestion.id];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle>MAIP-Bedarfs-Analysator</DialogTitle>
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">
                  Frage {currentQuestionIndex + 1} von {questions.length}
                </span>
                <Progress value={progress} className="w-32" />
              </div>
            </DialogHeader>

            <div className="space-y-6 pt-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">{currentQuestion.question_text}</h3>
                {currentQuestion.help_text && (
                  <p className="text-sm text-muted-foreground">{currentQuestion.help_text}</p>
                )}
              </div>

              <div className="space-y-3">
                {currentAnswers.map((answer) => (
                  <Card
                    key={answer.id}
                    className={`cursor-pointer transition-all ${
                      selectedAnswers[currentQuestion.id] === answer.id
                        ? "border-primary ring-2 ring-primary"
                        : "hover:border-primary/50"
                    }`}
                    onClick={() => handleAnswerSelect(currentQuestion.id, answer.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <p className="flex-1">{answer.answer_text}</p>
                        {selectedAnswers[currentQuestion.id] === answer.id && (
                          <CheckCircle2 className="w-5 h-5 text-primary ml-2" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentQuestionIndex === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button onClick={handleNext} disabled={!isAnswerSelected}>
                  {currentQuestionIndex === questions.length - 1 ? "Auswerten" : "Weiter"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Ihr Ergebnis</DialogTitle>
            </DialogHeader>

            <div className="space-y-6 pt-4">
              <Card className="border-primary">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold mb-2">{result.result_title}</h3>
                  <Badge variant="secondary" className="mb-4">
                    {result.result_persona_name}
                  </Badge>
                  <p className="text-muted-foreground whitespace-pre-line">{result.result_text}</p>
                </CardContent>
              </Card>

              {result.recommended_modules.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Empfohlene Module für Sie:</h4>
                  <div className="space-y-3">
                    {result.recommended_modules
                      .sort((a, b) => a.priority - b.priority)
                      .map((module) => (
                        <Card
                          key={module.id}
                          className="cursor-pointer hover:border-primary transition-all"
                          onClick={() => handleModuleClick(module.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <Badge variant="secondary" className="mt-0.5">
                                {module.priority}
                              </Badge>
                              <div className="flex-1">
                                <p className="font-semibold mb-1">
                                  {module.id === "datenschutz-kompass" && "Datenschutz-Kompass"}
                                  {module.id === "mitbestimmung" && "KI & Mitbestimmung"}
                                  {module.id === "main-program" && "Haupt-Programm (M1-5)"}
                                  {module.id === "ki-grundlagen" && "KI-Grundlagen (kostenlos)"}
                                </p>
                                {module.reason && (
                                  <p className="text-sm text-muted-foreground">{module.reason}</p>
                                )}
                              </div>
                              <ArrowRight className="w-5 h-5 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleReset} className="flex-1">
                  Erneut durchführen
                </Button>
                <Button onClick={() => onOpenChange(false)} className="flex-1">
                  Schließen
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
